/**
 * Couche formulaire admin des slides : chargement workspace, PATCH configurations,
 * validation select/médias, snapshots « non enregistré », aperçu widget.
 */
import type {
  WidgetItemConfigurationWidgetWorkspaceReadModel,
  WidgetWorkspaceWidgetWorkspaceReadModel
} from '@r/api/generated/client.schemas.ts';
import type { WidgetItemConfigurationWidgetWorkspacePatchJsonMergePatchModel } from '@r/api/generated/client.schemas.ts';
import type { UploadedFile } from '@r/types/index.ts';
import {
  getMediaIdFromIri,
  getWidgetSlideCount,
  getWidgetSlidesFromSlideValues,
  resolveWidgetAssetUrl,
  type WidgetSlideContent
} from '@r/helpers/widget-workspace-slides.ts';

export type SlideFormValues = Record<string, unknown>;

export interface ItemDefinition {
  id?: number;
  itemKey: string;
  itemLabel: string;
  itemType: string;
  defaultValue?: string | null;
  isRequired: boolean;
  maxLength?: number;
  sortOrder: number;
  itemDescription?: string | null;
  itemPlaceholder?: string | null;
  options?: string[] | null;
}

const getSelectOptions = (itemDef: ItemDefinition): string[] =>
  itemDef.options?.filter((option) => option.length > 0) ?? [];

/** Valeur select valide pour le formulaire (évite `''` qui casse z.enum). */
export const resolveSelectFieldValue = (itemDef: ItemDefinition, value: unknown): string => {
  const options = getSelectOptions(itemDef);
  if (typeof value === 'string' && value.length > 0 && options.includes(value)) {
    return value;
  }
  return itemDef.defaultValue ?? options[0] ?? '';
};

export const normalizeSlideSelectValues = (
  values: SlideFormValues,
  itemDefinitions: ItemDefinition[]
): SlideFormValues => {
  const next = { ...values };
  for (const itemDef of itemDefinitions) {
    if (itemDef.itemType !== 'select') continue;
    next[itemDef.itemKey] = resolveSelectFieldValue(itemDef, next[itemDef.itemKey]);
  }
  return next;
};

/** Valeurs initiales d’une slide (défauts widget ou chaîne vide pour les médias). */
export const getDefaultSlideValues = (itemDefinitions: ItemDefinition[]): SlideFormValues =>
  normalizeSlideSelectValues(
    itemDefinitions.reduce((acc, itemDef) => {
      if (itemDef.itemType === 'media') {
        acc[itemDef.itemKey] = '';
        return acc;
      }
      if (itemDef.itemType === 'select') {
        acc[itemDef.itemKey] = resolveSelectFieldValue(itemDef, itemDef.defaultValue);
        return acc;
      }
      acc[itemDef.itemKey] = itemDef.defaultValue || '';
      return acc;
    }, {} as SlideFormValues),
    itemDefinitions
  );

/** Index de slide le plus élevé présent dans les configurations serveur. */
export const getMaxSlideIndexFromConfigurations = (
  configurations: WidgetItemConfigurationWidgetWorkspaceReadModel[] | undefined
): number => {
  if (!configurations?.length) return 0;
  return Math.max(...configurations.map((config) => config.slideIndex ?? 1));
};

/**
 * Hydrate toutes les slides et leur nombre à partir d’un workspace existant.
 * Pour les champs média, `configValue` côté API est l’IRI `/medias/{id}` (conservée telle quelle dans le formulaire).
 */
export const loadSlidesFromWorkspace = (
  workspace: WidgetWorkspaceWidgetWorkspaceReadModel,
  itemDefinitions: ItemDefinition[]
): { slidesData: Record<number, SlideFormValues>; slideCount: number } => {
  const configurations = workspace.widgetItemConfigurations ?? [];
  const slideCount = getWidgetSlideCount(configurations);
  const slidesData: Record<number, SlideFormValues> = {};

  for (let slideIndex = 1; slideIndex <= slideCount; slideIndex++) {
    slidesData[slideIndex] = getDefaultSlideValues(itemDefinitions);
  }

  const definitionByKey = new Map(itemDefinitions.map((itemDef) => [itemDef.itemKey, itemDef]));

  for (const config of configurations) {
    const itemKey = config.widgetItemDefinition.itemKey;
    const itemDef = definitionByKey.get(itemKey);
    if (!itemDef) continue;

    slidesData[config.slideIndex] ??= getDefaultSlideValues(itemDefinitions);
    const rawValue = config.configValue ?? '';
    slidesData[config.slideIndex][itemKey] =
      itemDef.itemType === 'select' ? resolveSelectFieldValue(itemDef, rawValue) : rawValue;
  }

  for (let slideIndex = 1; slideIndex <= slideCount; slideIndex++) {
    slidesData[slideIndex] = normalizeSlideSelectValues(
      slidesData[slideIndex] ?? getDefaultSlideValues(itemDefinitions),
      itemDefinitions
    );
  }

  return { slidesData, slideCount };
};

/** Réordonne les clés 1…n après suppression d’une slide (données ou IRIs). */
export const reindexSlidesAfterRemoval = <T>(
  slidesData: Record<number, T>,
  slideCount: number,
  removedSlideIndex: number
): Record<number, T> => {
  const reindexedSlides: Record<number, T> = {};
  let nextIndex = 1;

  for (let slideIndex = 1; slideIndex <= slideCount; slideIndex++) {
    if (slideIndex === removedSlideIndex) continue;
    reindexedSlides[nextIndex] = slidesData[slideIndex] ?? ({} as T);
    nextIndex += 1;
  }

  return reindexedSlides;
};

/** Transforme un média résolu par l’API en fichier affiché par le FileUploader. */
export const getMediaUploadedFiles = (
  iri: unknown,
  pathUri: string | null | undefined
): UploadedFile[] => {
  if (typeof iri !== 'string' || !iri || !pathUri) return [];

  const fileName = (pathUri.split('/').pop() || pathUri).split('?')[0];
  return [
    {
      name: fileName,
      type: 'image/*',
      iri,
      pathUri: resolveWidgetAssetUrl(pathUri) ?? pathUri
    }
  ];
};

/** Indique si le champ média a une valeur (fichier, chemin ou IRI non vide). */
export const isMediaFormValueFilled = (value: unknown): boolean => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return value instanceof File;
};

/** Détermine si la définition correspond à un champ image (type media ou clé dédiée). */
export const isMediaItemDefinition = (itemDef: ItemDefinition): boolean =>
  itemDef.itemType === 'media' || itemDef.itemKey === 'background_image';

/** Champ obligatoire selon la définition widget renvoyée par l’API. */
export const isSlideFieldRequired = (itemDef: ItemDefinition): boolean => itemDef.isRequired;

/** Indique si la valeur formulaire est renseignée pour une définition donnée. */
export const isSlideFormValueFilled = (itemDef: ItemDefinition, value: unknown): boolean => {
  if (isMediaItemDefinition(itemDef)) {
    return isMediaFormValueFilled(value);
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== undefined && value !== null && value !== '';
};

/** Retourne l’IRI API Platform de la définition de champ, ou null si id manquant. */
export const getWidgetItemDefinitionIri = (itemDef: ItemDefinition): string | null => {
  if (itemDef.id === undefined || itemDef.id === null) return null;
  return `/widget_item_definitions/${itemDef.id}`;
};

/** N’envoie pas de `configValue` vide (évite 422 sur médias / champs requis). */
export const shouldIncludeFieldInPatch = (_itemDef: ItemDefinition, configValue: string): boolean =>
  configValue.trim().length > 0;

/**
 * Construit le tableau `patchConfigurations` pour une slide (PATCH workspace).
 * Champs média : `configValue` et `media` reçoivent la même IRI `/medias/{id}`.
 */
export const buildPatchConfigurations = (
  itemDefinitions: ItemDefinition[],
  processedValues: SlideFormValues
): WidgetItemConfigurationWidgetWorkspacePatchJsonMergePatchModel[] => {
  return itemDefinitions.flatMap((itemDef) => {
    const widgetItemDefinition = getWidgetItemDefinitionIri(itemDef);
    if (!widgetItemDefinition) return [];

    const rawValue = processedValues[itemDef.itemKey];
    const configValue =
      itemDef.itemType === 'select'
        ? resolveSelectFieldValue(itemDef, rawValue)
        : typeof rawValue === 'string'
          ? rawValue
          : '';

    if (!shouldIncludeFieldInPatch(itemDef, configValue)) {
      return [];
    }

    const configuration: WidgetItemConfigurationWidgetWorkspacePatchJsonMergePatchModel = {
      widgetItemDefinition,
      configValue
    };

    if (isMediaItemDefinition(itemDef)) {
      if (getMediaIdFromIri(configValue)) {
        configuration.media = configValue;
      }
    }

    return [configuration];
  });
};

/** Slide indexes that existed on the server but were removed in the editor. */
export const getRemovedSlideIndexes = (
  savedSlideCount: number,
  existingConfigurations: WidgetItemConfigurationWidgetWorkspaceReadModel[] | undefined
): number[] => {
  const previousMaxSlideIndex = getMaxSlideIndexFromConfigurations(existingConfigurations);
  const removed: number[] = [];

  for (let slideIndex = savedSlideCount + 1; slideIndex <= previousMaxSlideIndex; slideIndex++) {
    removed.push(slideIndex);
  }

  return removed;
};

/** Vérifie qu’un champ média obligatoire (API) a bien `media` dans le payload avant envoi. */
export const findMissingMediaIriInPatch = (
  itemDefinitions: ItemDefinition[],
  patchConfigurations: WidgetItemConfigurationWidgetWorkspacePatchJsonMergePatchModel[]
): ItemDefinition | undefined => {
  const patchByDefinition = new Map(
    patchConfigurations.map((configuration) => [configuration.widgetItemDefinition, configuration])
  );

  for (const itemDef of itemDefinitions) {
    if (!isSlideFieldRequired(itemDef)) continue;

    const widgetItemDefinition = getWidgetItemDefinitionIri(itemDef);
    if (!widgetItemDefinition) continue;

    const patch = patchByDefinition.get(widgetItemDefinition);
    if (!patch?.configValue?.trim()) continue;
    if (!patch.media) return itemDef;
  }

  return undefined;
};

const normalizeSnapshotValue = (value: unknown): unknown => {
  if (value instanceof File) {
    return {
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified
    };
  }

  if (Array.isArray(value)) {
    return value.map(normalizeSnapshotValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([key, nestedValue]) => [key, normalizeSnapshotValue(nestedValue)])
    );
  }

  return value ?? '';
};

/** Fusionne données persistées + slide active (formulaire en cours). */
export const mergeEditorSlidesData = (
  slidesCount: number,
  allSlidesData: Record<number, SlideFormValues>,
  currentSlide: number,
  currentSlideValues: SlideFormValues,
  itemDefinitions: ItemDefinition[]
): Record<number, SlideFormValues> => {
  const defaultSlideValues = getDefaultSlideValues(itemDefinitions);
  const merged: Record<number, SlideFormValues> = {};

  for (let slideIndex = 1; slideIndex <= slidesCount; slideIndex++) {
    merged[slideIndex] = {
      ...defaultSlideValues,
      ...(allSlidesData[slideIndex] ?? {}),
      ...(slideIndex === currentSlide ? currentSlideValues : {})
    };
  }

  return merged;
};

/** Slides widget pour l’aperçu instantané (état formulaire non enregistré). */
export const getEditorPreviewWidgetSlides = (
  slidesCount: number,
  allSlidesData: Record<number, SlideFormValues>,
  currentSlide: number,
  currentSlideValues: SlideFormValues,
  itemDefinitions: ItemDefinition[]
): WidgetSlideContent[] => {
  const merged = mergeEditorSlidesData(
    slidesCount,
    allSlidesData,
    currentSlide,
    currentSlideValues,
    itemDefinitions
  );
  return getWidgetSlidesFromSlideValues(slidesCount, merged);
};

/** Snapshot sérialisé de toutes les slides pour détecter les changements non enregistrés. */
export const createSlidesSnapshot = (
  slidesCount: number,
  allSlidesData: Record<number, SlideFormValues>,
  currentSlide: number,
  currentSlideValues: SlideFormValues,
  itemDefinitions: ItemDefinition[]
): string => {
  const merged = mergeEditorSlidesData(
    slidesCount,
    allSlidesData,
    currentSlide,
    currentSlideValues,
    itemDefinitions
  );

  const slides: Record<number, Record<string, unknown>> = {};
  for (let slideIndex = 1; slideIndex <= slidesCount; slideIndex++) {
    slides[slideIndex] = Object.fromEntries(
      itemDefinitions.map((itemDefinition) => [
        itemDefinition.itemKey,
        normalizeSnapshotValue(merged[slideIndex]?.[itemDefinition.itemKey])
      ])
    );
  }

  return JSON.stringify({
    slideCount: slidesCount,
    slides
  });
};
